/**
 * ChatSettingsPanel — system prompt editor, tool config, model settings,
 * and version history with rollback + diff for the AI Chat system.
 */
import { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Typography, Paper, Chip, Switch,
  FormControlLabel, Select, MenuItem, Alert, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, Tooltip, IconButton,
  Menu, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  Save, Restore, ExpandMore, History, CompareArrows, MoreVert, Visibility, WifiTethering,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { PromptDiffDialog } from './PromptDiffDialog';
import * as chatApi from '../../services/chatService';
import type { ChatSettings, SettingsHistoryEntry, ToolInfo } from '../../services/chatService';

export const ChatSettingsPanel = () => {
  const theme = useTheme();
  const [settings, setSettings] = useState<ChatSettings | null>(null);
  const [tools, setTools] = useState<ToolInfo[]>([]);
  const [history, setHistory] = useState<SettingsHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Editable fields
  const [prompt, setPrompt] = useState('');
  const [enabledTools, setEnabledTools] = useState<string[]>([]);
  const [model, setModel] = useState('');
  const [maxTokens, setMaxTokens] = useState(4096);
  const [effort, setEffort] = useState('high');

  // Connection diagnostics
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<chatApi.AiDiagnosticsResult | null>(null);

  // Diff dialog
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffLeft, setDiffLeft] = useState({ label: '', text: '' });
  const [diffRight, setDiffRight] = useState({ label: '', text: '' });

  // Version action menu
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuVersion, setMenuVersion] = useState<SettingsHistoryEntry | null>(null);

  // Compare mode — select two versions
  const [compareMode, setCompareMode] = useState(false);
  const [compareSelection, setCompareSelection] = useState<SettingsHistoryEntry[]>([]);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, t, h] = await Promise.all([
        chatApi.getChatSettings(),
        chatApi.getAvailableTools(),
        chatApi.getChatSettingsHistory(),
      ]);
      setSettings(s);
      setTools(t);
      setHistory(h);
      setPrompt(s.SystemPrompt);
      setEnabledTools(JSON.parse(s.EnabledTools || '[]'));
      setModel(s.Model);
      setMaxTokens(s.MaxTokensPerMessage);
      setEffort(s.Effort || 'high');
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load settings' });
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const updated = await chatApi.updateChatSettings({
        SystemPrompt: prompt,
        EnabledTools: JSON.stringify(enabledTools),
        Model: model,
        MaxTokensPerMessage: maxTokens,
        Effort: effort,
        changeNote: changeNote || 'Settings updated',
      });
      setSettings(updated);
      setChangeNote('');
      setMessage({ type: 'success', text: 'Settings saved. New conversations will use the updated config.' });
      const h = await chatApi.getChatSettingsHistory();
      setHistory(h);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    }
    setSaving(false);
  };

  const handleRollback = async (version: number) => {
    setSaving(true);
    setMessage(null);
    setMenuAnchor(null);
    try {
      const updated = await chatApi.rollbackChatSettings(version);
      setSettings(updated);
      setPrompt(updated.SystemPrompt);
      setEnabledTools(JSON.parse(updated.EnabledTools || '[]'));
      setModel(updated.Model);
      setMaxTokens(updated.MaxTokensPerMessage);
      setEffort(updated.Effort || 'high');
      setMessage({ type: 'success', text: `Rolled back to version ${version}. Save to confirm.` });
      const h = await chatApi.getChatSettingsHistory();
      setHistory(h);
    } catch (err) {
      setMessage({ type: 'error', text: 'Rollback failed' });
    }
    setSaving(false);
  };

  const handleDiffVsCurrent = (entry: SettingsHistoryEntry) => {
    setMenuAnchor(null);
    setDiffLeft({ label: `v${entry.Version} — ${entry.ChangeNote || 'No note'}`, text: entry.SystemPrompt });
    setDiffRight({ label: 'Current (live)', text: settings?.SystemPrompt || '' });
    setDiffOpen(true);
  };

  const handleDiffVsEditor = (entry: SettingsHistoryEntry) => {
    setMenuAnchor(null);
    setDiffLeft({ label: `v${entry.Version} — ${entry.ChangeNote || 'No note'}`, text: entry.SystemPrompt });
    setDiffRight({ label: 'Editor (unsaved)', text: prompt });
    setDiffOpen(true);
  };

  const handleCompareToggle = (entry: SettingsHistoryEntry) => {
    setCompareSelection((prev) => {
      const exists = prev.find((e) => e.Version === entry.Version);
      if (exists) return prev.filter((e) => e.Version !== entry.Version);
      const next = [...prev, entry].slice(-2);
      if (next.length === 2) {
        const [a, b] = next.sort((x, y) => x.Version - y.Version);
        setDiffLeft({ label: `v${a.Version} — ${a.ChangeNote || 'No note'}`, text: a.SystemPrompt });
        setDiffRight({ label: `v${b.Version} — ${b.ChangeNote || 'No note'}`, text: b.SystemPrompt });
        setDiffOpen(true);
        setCompareMode(false);
        return [];
      }
      return next;
    });
  };

  const toggleTool = (name: string) => {
    setEnabledTools((prev) =>
      prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name],
    );
  };

  const hasChanges = settings && (
    prompt !== settings.SystemPrompt ||
    JSON.stringify(enabledTools) !== settings.EnabledTools ||
    model !== settings.Model ||
    maxTokens !== settings.MaxTokensPerMessage ||
    effort !== (settings.Effort || 'high')
  );

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await chatApi.testAiConnection();
      setTestResult(result);
    } catch {
      setTestResult({ keyConfigured: false, probe: 'other', model, detail: 'The diagnostics request itself failed — is the server reachable?' });
    }
    setTesting(false);
  };

  const testSeverity: 'success' | 'warning' | 'error' =
    testResult?.probe === 'ok' ? 'success' : testResult?.probe === 'rate_limited' ? 'warning' : 'error';

  const testMessage = (() => {
    if (!testResult) return '';
    if (!testResult.keyConfigured) {
      return 'The server has no ANTHROPIC_API_KEY configured — add it in the Azure App Service settings (Configuration → Application settings) and restart the app.';
    }
    switch (testResult.probe) {
      case 'ok': return `Everything works — the key is valid and ${testResult.model} responded.`;
      case 'auth_error': return 'The server has a key, but the AI service rejected it — the key is expired or revoked. Replace ANTHROPIC_API_KEY in the Azure App Service settings.';
      case 'model_not_found': return `The key works, but the configured model "${testResult.model}" was not found. Pick a different model above and save.`;
      case 'rate_limited': return 'The key works, but the AI service is rate-limiting requests right now — try again in a minute.';
      default: return `The connection test failed: ${testResult.detail || 'unknown error'}`;
    }
  })();

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>;

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 2 }}>
        AI Chat Settings
      </Typography>

      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      {/* System Prompt */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: alpha(theme.palette.divider, 0.4) }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>System Prompt</Typography>
        <TextField
          fullWidth multiline minRows={8} maxRows={20}
          value={prompt} onChange={(e) => setPrompt(e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.82rem', fontFamily: 'monospace' } }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {prompt.length} characters. Changes apply to new conversations only.
        </Typography>
      </Paper>

      {/* Model + Effort + Tokens */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Paper variant="outlined" sx={{ flex: 1, p: 2, borderRadius: 2, borderColor: alpha(theme.palette.divider, 0.4) }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Model</Typography>
          <Select fullWidth size="small" value={model} onChange={(e) => setModel(e.target.value)} sx={{ fontSize: '0.82rem' }}>
            <MenuItem value="claude-sonnet-5">Claude Sonnet 5 (recommended)</MenuItem>
            <MenuItem value="claude-haiku-4-5-20251001">Claude Haiku 4.5</MenuItem>
            <MenuItem value="claude-sonnet-4-6">Claude Sonnet 4.6 (legacy)</MenuItem>
            <MenuItem value="claude-opus-4-6">Claude Opus 4.6 (legacy)</MenuItem>
          </Select>
        </Paper>
        {model.startsWith('claude-sonnet-5') && (
          <Paper variant="outlined" sx={{ flex: 1, p: 2, borderRadius: 2, borderColor: alpha(theme.palette.divider, 0.4) }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Effort</Typography>
            <Select fullWidth size="small" value={effort} onChange={(e) => setEffort(e.target.value)} sx={{ fontSize: '0.82rem' }}>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High (default)</MenuItem>
              <MenuItem value="xhigh">Extra high</MenuItem>
              <MenuItem value="max">Max</MenuItem>
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              How hard the model thinks — higher = better answers, more tokens.
            </Typography>
          </Paper>
        )}
        <Paper variant="outlined" sx={{ flex: 1, p: 2, borderRadius: 2, borderColor: alpha(theme.palette.divider, 0.4) }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Max Tokens / Message</Typography>
          <Select fullWidth size="small" value={maxTokens} onChange={(e) => setMaxTokens(Number(e.target.value))} sx={{ fontSize: '0.82rem' }}>
            <MenuItem value={2048}>2,048</MenuItem>
            <MenuItem value={4096}>4,096</MenuItem>
            <MenuItem value={8192}>8,192</MenuItem>
            <MenuItem value={16000}>16,000</MenuItem>
            <MenuItem value={16384}>16,384</MenuItem>
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            On Sonnet 5 this cap includes the model&rsquo;s thinking, not just the visible answer — 16,000+ recommended.
          </Typography>
        </Paper>
      </Box>

      {/* AI connection diagnostics */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, borderColor: alpha(theme.palette.divider, 0.4) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>AI Connection</Typography>
          <Button variant="outlined" size="small" startIcon={testing ? <CircularProgress size={14} /> : <WifiTethering />}
            onClick={handleTestConnection} disabled={testing}>
            {testing ? 'Testing...' : 'Test AI connection'}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Checks whether the server has an Anthropic API key and whether the configured model answers a tiny test request.
        </Typography>
        {testResult && (
          <Alert severity={testSeverity} sx={{ mt: 1.5 }} onClose={() => setTestResult(null)}>
            {testMessage}
          </Alert>
        )}
      </Paper>

      {/* Save bar */}
      <Paper variant="outlined" sx={{
        p: 1.5, mb: 3, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5,
        borderColor: hasChanges ? theme.palette.warning.main : alpha(theme.palette.divider, 0.4),
        backgroundColor: hasChanges ? alpha(theme.palette.warning.main, 0.04) : 'transparent',
      }}>
        <TextField size="small" placeholder="Change note (optional)" value={changeNote}
          onChange={(e) => setChangeNote(e.target.value)}
          sx={{ flex: 1, '& .MuiOutlinedInput-root': { fontSize: '0.82rem' } }} />
        <Button variant="contained" size="small" startIcon={<Save />}
          onClick={handleSave} disabled={!hasChanges || saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Paper>

      {/* Version History */}
      <Accordion disableGutters elevation={0} sx={{
        border: `1px solid ${alpha(theme.palette.divider, 0.4)}`, borderRadius: '8px !important',
        '&:before': { display: 'none' },
      }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              Version History ({history.length})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {/* Compare mode toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
            <Button size="small" variant={compareMode ? 'contained' : 'outlined'}
              startIcon={<CompareArrows sx={{ fontSize: 14 }} />}
              onClick={() => { setCompareMode((c) => !c); setCompareSelection([]); }}
              sx={{ fontSize: '0.72rem', textTransform: 'none' }}>
              {compareMode
                ? compareSelection.length === 0 ? 'Select first version...' : `Selected v${compareSelection[0].Version} — select second`
                : 'Compare two versions'}
            </Button>
            {compareMode && (
              <Button size="small" onClick={() => { setCompareMode(false); setCompareSelection([]); }}
                sx={{ fontSize: '0.72rem', textTransform: 'none' }}>Cancel</Button>
            )}
          </Box>

          {history.length === 0 && (
            <Typography variant="caption" color="text.secondary">No version history yet.</Typography>
          )}
          {history.map((h) => {
            const isSelected = compareSelection.some((s) => s.Version === h.Version);
            return (
              <Box key={h.HistoryKey} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, py: 1,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                borderLeft: isSelected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                pl: 1, cursor: compareMode ? 'pointer' : 'default',
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.03) },
              }}
                onClick={compareMode ? () => handleCompareToggle(h) : undefined}
              >
                <Chip label={`v${h.Version}`} size="small" variant={isSelected ? 'filled' : 'outlined'}
                  color={isSelected ? 'primary' : 'default'}
                  sx={{ height: 24, fontSize: '0.72rem', fontWeight: 700, minWidth: 40 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                    {h.ChangeNote || 'No note'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                      {h.ChangedBy} · {new Date(h.ChangedAt).toLocaleString('en-CA')}
                    </Typography>
                    <Chip label={h.Model} size="small" variant="outlined"
                      sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.75 } }} />
                  </Box>
                </Box>
                {!compareMode && (
                  <Box sx={{ display: 'flex', gap: 0.25 }}>
                    <Tooltip title="Compare with current live prompt">
                      <IconButton size="small" onClick={() => handleDiffVsCurrent(h)}>
                        <CompareArrows sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuVersion(h); }}>
                      <MoreVert sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            );
          })}
        </AccordionDetails>
      </Accordion>

      {/* Enabled Tools */}
      <Accordion disableGutters elevation={0} sx={{
        border: `1px solid ${alpha(theme.palette.divider, 0.4)}`, borderRadius: '8px !important',
        '&:before': { display: 'none' }, mt: 2,
      }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
            Enabled Tools ({enabledTools.length}/{tools.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {(['data', 'visualization', 'analytics', 'documents', 'context'] as const).map((cat) => {
            const catTools = tools.filter((t) => (t.category || 'data') === cat);
            if (catTools.length === 0) return null;
            const catLabels: Record<string, string> = {
              data: 'Data Access', visualization: 'Visualization', analytics: 'Analytics',
              documents: 'Documents & Export', context: 'Context & Help',
            };
            return (
              <Box key={cat} sx={{ mb: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5 }}>
                  {catLabels[cat]}
                </Typography>
                {catTools.map((tool) => (
                  <FormControlLabel key={tool.name}
                    control={<Switch size="small" checked={enabledTools.includes(tool.name)} onChange={() => toggleTool(tool.name)} />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{tool.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.3 }}>
                          {tool.description.substring(0, 100)}{tool.description.length > 100 ? '...' : ''}
                        </Typography>
                      </Box>
                    }
                    sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5, ml: 0 }}
                  />
                ))}
              </Box>
            );
          })}
        </AccordionDetails>
      </Accordion>

      {/* Version action menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
        PaperProps={{ sx: { minWidth: 220 } }}>
        {menuVersion && [
          <MenuItem key="diff-current" onClick={() => handleDiffVsCurrent(menuVersion)} sx={{ fontSize: '0.82rem' }}>
            <ListItemIcon><CompareArrows sx={{ fontSize: 16 }} /></ListItemIcon>
            <ListItemText>Compare with live prompt</ListItemText>
          </MenuItem>,
          <MenuItem key="diff-editor" onClick={() => handleDiffVsEditor(menuVersion)} sx={{ fontSize: '0.82rem' }}>
            <ListItemIcon><Visibility sx={{ fontSize: 16 }} /></ListItemIcon>
            <ListItemText>Compare with editor</ListItemText>
          </MenuItem>,
          <MenuItem key="rollback" onClick={() => handleRollback(menuVersion.Version)} disabled={saving} sx={{ fontSize: '0.82rem' }}>
            <ListItemIcon><Restore sx={{ fontSize: 16 }} /></ListItemIcon>
            <ListItemText>Rollback to this version</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      {/* Diff dialog */}
      <PromptDiffDialog open={diffOpen} onClose={() => setDiffOpen(false)}
        leftLabel={diffLeft.label} rightLabel={diffRight.label}
        leftText={diffLeft.text} rightText={diffRight.text} />
    </Box>
  );
};
